// Common components for editable lists
import { useEffect, useRef, useState } from "react";
import {
	CoreButton,
	CoreChip,
	CoreIconButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreTextField,
	CoreTextFieldProps,
	EllipsisTooltip,
} from "../../../core";
import i18nPackage from "../../../i18n/i18n";
import { EditableListRow } from "../hooks/useEditableList";
import styles from "./EditableList.module.css";
import { ListItemText, SvgIcon, Typography } from "@mui/material";
import { SelectAllContainer, SelectAllContainerProps, SelectedBadge } from "./OptionListsComponents";
import Delete from "../../../core/images/close-x.svg?react";
import { CoreButtonProps } from "../../../core/button/Button";

type EditableListToolbarProps = {
	searchQuery: string;
	onSearchChange: (value: string) => void;
	handleAdd: (value: string) => void;
	canAddOrEdit: (value: string) => boolean;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	placeholder: string;

	slotProps?: {
		button?: Omit<CoreButtonProps, "onClick" | "children" | "disabled"> & {
			text?: string;
		};
		textfield?: Omit<CoreTextFieldProps, "label" | "placeholder" | "onChange" | "onKeyDown" | "value">;
	};
};

export function EditableListToolbar({
	canAddOrEdit,
	handleAdd,
	onSearchChange,
	searchQuery,
	placeholder,
	slotProps,
	onKeyDown,
}: EditableListToolbarProps) {
	const internalInputRef = useRef<HTMLInputElement | null>(null);
	const externalInputRef = slotProps?.textfield?.inputRef;

	const setMergedRef = (element: HTMLInputElement | null) => {
		// Sync Internal Ref
		internalInputRef.current = element;

		// Sync External Ref
		if (!externalInputRef) return;

		if (typeof externalInputRef === "function") {
			externalInputRef(element);
		} else {
			// Check if it's a valid object ref before assigning
			const refObject = externalInputRef as React.MutableRefObject<HTMLInputElement | null>;
			refObject.current = element;
		}
	};

	const buttonProps = slotProps?.button;
	const textfieldProps = slotProps?.textfield;

	const handleAddAndFocus = () => {
		handleAdd(searchQuery);

		// Use a slight delay to ensure the DOM has updated (searchQuery cleared, etc.)
		// before pulling focus back
		requestAnimationFrame(() => {
			internalInputRef.current?.focus();
		});
	};

	return (
		<div className={styles.textFieldRow}>
			<CoreTextField
				{...textfieldProps}
				inputRef={setMergedRef}
				label={placeholder}
				value={searchQuery}
				onChange={(e) => onSearchChange(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();

						if (canAddOrEdit(searchQuery)) {
							handleAddAndFocus();
						}
					}
					onKeyDown?.(e);
				}}
				className={`${styles.addTextfield} ${textfieldProps?.className || ""}`}
			/>
			<CoreButton
				onClick={handleAddAndFocus}
				className={`${styles.addRemoveButton} ${buttonProps?.className || ""}`}
				disabled={!canAddOrEdit(searchQuery)}
			>
				{buttonProps?.text ?? i18nPackage.t("editQuizPage.editableListTSX.add")}
			</CoreButton>
		</div>
	);
}

type EditableListCellProps<T extends object> = {
	row: EditableListRow<T>;
	dataKey: keyof T;
	onUpdate?: (id: string, updated: Partial<T>) => void;
	onRemove?: (id: string) => void;
	canAddOrEdit: (value: string) => boolean;
	maxWidth?: number;
	slotProps?: {
		button?: {
			saveText?: string;
			removeText?: string;
		};
		badge?: {
			disabledLabel?: string;
			disabledTooltip?: string;
			selectedLabel?: string;
		};
	};
};

export function EditableListCell<T extends object>({
	row,
	dataKey,
	onUpdate,
	onRemove,
	canAddOrEdit,
	maxWidth = 500,
	slotProps,
}: EditableListCellProps<T>) {
	// Extract the initial value using the dataKey
	const initialValue = String(row[dataKey] || "");
	const [value, setValue] = useState(initialValue);
	const buttonProps = slotProps?.button;
	const badgeProps = slotProps?.badge;

	useEffect(() => {
		if (row.editing) {
			setValue(initialValue);
		}
	}, [row.editing, initialValue]);

	// VIEW MODE
	if (!row.editing || !onUpdate || !onRemove) {
		return (
			<div className={styles.cellContainer}>
				<EllipsisTooltip entry={initialValue} width={maxWidth}>
					<Typography className={styles.textValue} sx={{ maxWidth }}>
						{initialValue}
					</Typography>
				</EllipsisTooltip>
				{row.disabled && badgeProps?.disabledLabel && (
					<CoreChip
						chipColor="#F5E5F4"
						className={styles.badge}
						tooltip={row.disabled ? badgeProps?.disabledTooltip : undefined}
					>
						{badgeProps.disabledLabel}
					</CoreChip>
				)}
				{row.selected && badgeProps?.selectedLabel && <SelectedBadge text={badgeProps?.selectedLabel} />}
			</div>
		);
	}

	// EDIT MODE
	const handleSave = () => {
		const trimmed = value.trim();
		if (trimmed) {
			// We update ONLY the property defined by dataKey
			onUpdate(row.id, { [dataKey]: trimmed } as Partial<T>);
		}
	};

	return (
		<div className={styles.editRow}>
			<CoreTextField
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						handleSave();
					}
				}}
				autoFocus
				className={styles.editRowTextfield}
				ariaLabel={i18nPackage.t("editQuizPage.editableListTSX.discard", { initialValue })}
			/>
			<CoreButton onClick={() => onRemove(row.id)} color="cancel" className={styles.editRowButton}>
				{buttonProps?.removeText ?? i18nPackage.t("editQuizPage.editableListTSX.discard")}
			</CoreButton>
			<CoreButton onClick={handleSave} disabled={!canAddOrEdit(value)} color="primary" className={styles.editRowButton}>
				{buttonProps?.saveText ?? i18nPackage.t("editQuizPage.editableListTSX.save")}
			</CoreButton>
		</div>
	);
}

type EditableListMenuButtonProps = {
	onEdit?: () => void;
	onDelete?: () => void;
};

export function EditableListMenuButton({ onEdit, onDelete }: EditableListMenuButtonProps) {
	const [open, setOpen] = useState(false);

	if (!onEdit && !onDelete) return null;

	return (
		<CoreMenuButton
			menuShowing={open}
			setMenuShowing={setOpen}
			aria-label={i18nPackage.t("editQuizPage.editableListTSX.actionsAriaLabel")}
		>
			{onEdit && (
				<CoreMenuItem
					onClick={() => {
						setOpen(false);
						onEdit();
					}}
				>
					<ListItemText primary={i18nPackage.t("editQuizPage.editableListTSX.edit")} />
				</CoreMenuItem>
			)}
			{onDelete && (
				<CoreMenuItem
					onClick={() => {
						setOpen(false);
						onDelete();
					}}
				>
					<ListItemText primary={i18nPackage.t("editQuizPage.editableListTSX.delete")} />
				</CoreMenuItem>
			)}
		</CoreMenuButton>
	);
}

type EditableListRemoveButtonProps = {
	onClick: () => void;
	ariaLabel: string;
};

export function EditableListRemoveButton({ onClick, ariaLabel }: EditableListRemoveButtonProps) {
	return (
		<CoreIconButton variant="plain" onClick={onClick} className={styles.deleteButton} aria-label={ariaLabel}>
			<SvgIcon>
				<Delete />
			</SvgIcon>
		</CoreIconButton>
	);
}

type EditableSelectAllRowProps = {
	checked: boolean;
	disabled: boolean;
	onChange: (checked: boolean) => void;
	badge?: NonNullable<SelectAllContainerProps["slotProps"]>["badge"];
	button?: {
		onRemove: () => void;
		disabled: boolean;
	};
};

// Wrapper around SelectAllContainer
export function EditableSelectAllRow({ checked, disabled, onChange, button, badge }: EditableSelectAllRowProps) {
	return (
		<SelectAllContainer
			checked={checked}
			disabled={disabled}
			onChange={onChange}
			className={styles.selectAllContainer}
			slotProps={badge ? { badge } : undefined}
		>
			{button && (
				<CoreButton
					onClick={button.onRemove}
					color="cancel"
					className={styles.addRemoveButton}
					disabled={button.disabled}
				>
					{i18nPackage.t("editQuizPage.editableListTSX.delete")}
				</CoreButton>
			)}
		</SelectAllContainer>
	);
}
